import { APP_LANGUAGES_LIST, PLATFORM_LOGOS, resellerOnly } from '@/consts/app-installer'

export function filterAppLangs (appLangs) {
  if (!Array.isArray(appLangs) || appLangs.length === 0) {
    return APP_LANGUAGES_LIST.slice()
  }
  return appLangs.slice().reduce((acc, lang) => {
    const matchedLang = APP_LANGUAGES_LIST.find(l => lang.toLowerCase().includes(l.value.split('_')[0].toLowerCase()))
    if (matchedLang) {
      acc.push({
        value: lang,
        label: matchedLang.label,
      })
    }
    return acc
  }, [])
}

/**
 * https://www.softaculous.com/docs/api/api/#list-scripts
 * @typedef {number} ScriptID
 * @typedef {Object} ScriptInfo
 * @property {string} name - Human-readable name of the application.
 * @property {string} softname - Machine/slug name used by the API.
 * @property {string} desc - Short description of the application.
 * @property {number} ins - Installation flag or count (numeric).
 * @property {string} cat - Category of the application (e.g. "forums").
 * @property {string} type - Platform/type of the application (e.g. "php").
 * @property {string} ver - Version string.
 * @property {number} pre_down - Pre-download flag (0 or 1).
 *
 * Find and return only platforms from the provided list.
 * @param {Object.<ScriptID, ScriptInfo>} iscripts - The full list of scripts from Softaculous API.
 * @param {string[]} platforms - Array of platform slugs to filter by (lowercase).
 * @returns {Array.<{ScriptInfo && {resellerOnly: boolean}}>} Filtered and mapped array of platforms.
 */
export function filterAppPlatforms (iscripts, platforms) {
  const result = []
  const seen = new Set()

  for (const [id, script] of Object.entries(iscripts)) {
    const found = platforms.find(platform => script.softname.startsWith(platform) || script.name.toLowerCase().includes(platform))
    if (found && !seen.has(found)) {
      result.push({
        id: Number(id),
        name: script.name,
        type: script.type,
        version: script.ver,
        description: script.desc,
        softname: script.softname,
        resellerOnly: resellerOnly.includes(script.softname),
        logo: PLATFORM_LOGOS[found] || '',
      })
      seen.add(found)
    }
  }

  return result
}

/**
 * @typedef {Object} InstalledAppInfo
 * @property {string} insid - Installation identifier like "26_48711".
 * @property {number|string} [sid] - Script id (numeric or string).
 * @property {string} [ver] - Version string.
 * @property {number} [itime] - Installation time (unix timestamp).
 * @property {string} [softpath]
 * @property {string} [softurl]
 * @property {string} [adminurl]
 * @property {string} [softdomain]
 * @property {string} [softdb]
 * @property {string} [softdbuser]
 * @property {string} [softdbhost]
 * @property {string} [softdbpass]
 * @property {string} [display_softdbpass]
 * @property {string} [dbprefix]
 * @property {boolean} [dbcreated]
 * @property {Array<string>} [fileindex]
 * @property {string} [admin_username]
 * @property {string} [admin_email]
 * @property {string} [site_name]
 * @property {string} [script_name]
 * @property {Object} [live_ins] - If this is a staging installation, the live installation object.
 * @property {string} [is_staging] - Insid of the live install if this is a staging install.
 * // other softaculous fields may be present and will be preserved
 */

/**
 * Transform Softaculous "list-installed" response into a flat array of InstalledAppInfo.
 *
 * @param {Object} responseInstallations - installations object from response from https://www.softaculous.com/docs/api/api/#list-installed-script
 * @returns {InstalledAppInfo[]} Flattened array of installation objects.
 */
export function transformInstalledApps (responseInstallations) {
  const list = []
  for (const insData of Object.values(responseInstallations)) {
    for (const insInfo of Object.values(insData || {})) {
      list.push({
        ...insInfo,
        logo: resolveScriptLogo(insInfo.script_name),
        isWp: insInfo.script_name.toLowerCase() === 'wordpress',
      })
    }
  }

  return list
}

function resolveScriptLogo (scriptName) {
  const replaceScriptNames = new Map([
    ['wordpress', 'wp'],
    ['drupal core', 'drupal'],
  ])
  return PLATFORM_LOGOS[replaceScriptNames.get(scriptName.toLowerCase()) || scriptName.toLowerCase()] || 'default.svg'
}

/**
 * Compare two dot-separated version strings by numeric segments.
 *
 * Splits each version string on '.' and compares corresponding numeric parts
 * from left to right. Missing segments are treated as 0 (e.g. "1.0" == "1.0.0").
 * Returns 1 if v1 > v2, -1 if v1 < v2, or 0 if they are equal.
 *
 * @param {string} v1 - First version string (e.g. "1.2.3").
 * @param {string} v2 - Second version string (e.g. "1.2").
 * @returns {number} 1 if v1 is greater than v2, -1 if v1 is less than v2, 0 if equal.
 */
export function versionCompare (v1, v2) {
  const v1parts = v1.split('.').map(Number)
  const v2parts = v2.split('.').map(Number)
  const len = Math.max(v1parts.length, v2parts.length)
  for (let i = 0; i < len; i++) {
    const part1 = v1parts[i] || 0
    const part2 = v2parts[i] || 0
    if (part1 > part2) return 1
    if (part1 < part2) return -1
  }
  return 0
}
